// Centralized route table (url-conventions.md §6). Spanish-first, accent-free
// slugs for every authenticated surface. `login` stays English (universal).

export const ROUTES = {
  login: "/login",
  overview: "/",
  institutions: "/instituciones",
  institutionNew: "/instituciones/nueva",
  schools: "/escuelas",
  professors: "/profesores",
  // PRD-56 §FR-18 — add many from a pasted list or a CSV. A page, not a
  // dialog: a 300-row preview does not fit a sheet at 375 px.
  professorsAdd: "/profesores/agregar",
  // PRD-60 chunk B — "Usuarios": not every console account administers (a
  // viewer only reads). `/administradores` redirects here for one release.
  adminUsers: "/usuarios",
  // PRD-56 PR (4) — detail pages. Each tab is a path segment, so a link
  // lands on the tab it names; `/escuelas/:id/materias` (the subjects page of
  // PRD-60 chunk B) is now the school's first tab and keeps working.
  institution: "/instituciones/:institutionId/:tab?",
  institutionFor: (id: string, tab?: InstitutionTab) =>
    `/instituciones/${encodeURIComponent(id)}${tab ? `/${tab}` : ""}`,
  school: "/escuelas/:schoolId/:tab?",
  schoolFor: (id: string, tab?: SchoolTab) =>
    `/escuelas/${encodeURIComponent(id)}${tab ? `/${tab}` : ""}`,
  professor: "/profesores/:professorId",
  professorFor: (id: string) => `/profesores/${encodeURIComponent(id)}`,
  // PRD-56 §FR-15 — what the director owns: crest, academic cycle, trail.
  myInstitution: "/mi-institucion",
  // PRD-69 / ADR-0053 — "Materiales": Platonica superusers prepare, send and
  // grade inside a consenting professor's account. Sections and tabs are path
  // segments, like the detail pages, so a link lands where it names.
  staffMaterials: "/materiales-preparados/:section?",
  staffMaterialsFor: (section?: StaffMaterialsSection) =>
    `/materiales-preparados${section ? `/${section}` : ""}`,
  staffMaterialNew: "/materiales-preparados/nuevo",
  staffMaterial: "/materiales-preparados/material/:materialId/:tab?",
  staffMaterialFor: (id: string, tab?: StaffMaterialTab) =>
    `/materiales-preparados/material/${encodeURIComponent(id)}${tab ? `/${tab}` : ""}`,
  staffUpload: "/materiales-preparados/entrega/:uploadId",
  staffUploadFor: (id: string) => `/materiales-preparados/entrega/${encodeURIComponent(id)}`,
  settings: "/configuracion",
  // PRD-33 / ADR-0030 — public account-activation (set-password) page.
  // `activate` is the React Router path pattern; `activateFor` builds a
  // concrete link for a token.
  activate: "/activar/:token",
  activateFor: (token: string) => `/activar/${encodeURIComponent(token)}`,
  // Password-reset (admin) — public set-new-password page. Mirrors the
  // activation page; reuses the same /api/public/password-setup/{token}
  // endpoints. `passwordReset` is the route pattern; `passwordResetFor`
  // builds a concrete link.
  passwordReset: "/restablecer/:token",
  passwordResetFor: (token: string) => `/restablecer/${encodeURIComponent(token)}`,
} as const

// Accent-free Spanish slugs (url-conventions.md §4).
export const SCHOOL_TABS = ["materias", "profesores", "configuracion"] as const
export type SchoolTab = (typeof SCHOOL_TABS)[number]
export const INSTITUTION_TABS = ["salud", "configuracion", "bitacora"] as const
export type InstitutionTab = (typeof INSTITUTION_TABS)[number]
export const STAFF_MATERIALS_SECTIONS = ["materiales", "por-calificar", "profesores"] as const
export type StaffMaterialsSection = (typeof STAFF_MATERIALS_SECTIONS)[number]
export const STAFF_MATERIAL_TABS = ["copias", "entregas"] as const
export type StaffMaterialTab = (typeof STAFF_MATERIAL_TABS)[number]
